import * as React from 'react';
import { t } from '@reos/i18n';
import { parsePlaceLink } from '@reos/geo-input';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { AddressAutocomplete } from '../components/AddressAutocomplete.js';
import { MapPreview } from '../components/MapPreview.js';
import { FieldGrid, FormLayout } from '../components/form-layout.js';
import {
  CASE_TYPE_BY_DEAL_INTENT,
  CLIENT_ROLE_BY_DEAL_INTENT,
  PROPERTY_KIND_BY_DEAL_KIND,
  emptyDealAddress,
  emptyDealClient,
  type DealAddress,
  type DealClient,
  type DealDraft,
  type DealIntent,
  type DealKind,
} from '../lib/wizard-model.js';
import { isValidIco, lookupAres } from '../lib/ares.js';
import { DomainApiError } from '../lib/api-error.js';
import { focusField, kodChyby } from '../lib/form-errors.js';
import { WIZARD_TEXTS } from '../lib/texts.js';
import { V4Steps, type V4Step } from '../components/ux-v4.js';
import { wgs84ToSjtsk } from '../lib/parcel-map-crs.js';
import {
  fetchParcelAtPoint,
  fetchParcelNeighbours,
  type HouseLandNeighboursDto,
  type ParcelLookupDto,
} from '../lib/parcel-map-api.js';
import { fetchListingPrefill } from '../lib/listing-api.js';
import {
  createCaseOverProperty,
  parcelsAlreadyUsedPropertyIds,
  samePropertyCandidate,
  type CaseClientInput,
  type CaseFactInput,
  type NewPropertyParcel,
  type SamePropertyCandidate,
} from '../lib/case-create.js';
import { findPartyByEmail } from '../lib/parties-api.js';
/* Krok P8F6 bod 1: popisek udaje je TYZ katalogovy klic jako v naboru. */
import { fieldLabel } from '../components/field-intake/values.js';
import { parseMoneyInput } from '../lib/money-text.js';
import { useLeaveWarning } from '../lib/leave-guard.js';
import { formatMoney } from '../lib/properties.js';
import { useSession } from '../stores/session.js';
import {
  classifyLocationEntry,
  expandLocationLink,
  formatAccuracy,
  isAccuracyWeak,
  locateFailureOf,
  type LocateFailure,
  type LocationEntry,
} from '../lib/location-entry.js';

/**
 * ZALOZIT ZAKAZKU — JEDNA KRATKA OBRAZOVKA (krok UXZ1, nalezy 13-14 auditu GPT).
 *
 * Do UXZ1 tu stal ctyrkrokovy pruvodce, ktery na konci SPUSTIL PROVERENI
 * (`StartAuditModal` -> `POST /v1/cases/:id/audit-runs`). Makler ale pri
 * prvnim hovoru s klientem potrebuje jen zaevidovat pripad: zamer, druh,
 * misto, klienta, cenovou predstavu a poznamku. Provereni je DALSI KROK a
 * nabizi ho detail zakazky (`routes/zakazka-detail.tsx`, karta „Další krok" u
 * zakazky bez behu) — tatáž cesta jako „Prověřit znovu", nic noveho.
 *
 * Zalozeni jde pres `lib/case-create.ts` (nemovitost -> zakazka), tedy TOUTEZ
 * posloupnosti jako „Převzít do zakázky" z tipu akvizice. Zakazka vznika ve
 * fazi `intake` = „Založení" (viz `lib/case-phases.ts`), takze rozpracovany
 * pripad se ulozi tim, ze se zalozi — zadny druhy stav „koncept" nevznika.
 */

/**
 * Krok GPS1: `/zakazky/novy?lat=…&lon=…` — poloha z odkazu. `validateSearch`
 * v routeru je zamerne identita (stejne jako u `/mapa`), rozklad zde dela
 * `parsePlaceLink`, ktery vi, ktery parametr je souradnice a ktery text.
 */
const zakazkyNovyRouteApi = getRouteApi('/zakazky/novy');

type FormState = DealDraft;

function initialState(): FormState {
  return {
    kind: 'LAND',
    intent: 'SALE',
    address: emptyDealAddress(),
    parcelNumber: '',
    cadastralArea: '',
    landRegistryNumber: '',
    listingUrl: '',
    unitNumber: '',
    layout: '',
    notes: '',
    client: emptyDealClient(),
    priceExpectation: '',
    priceIsAsking: false,
  };
}

/**
 * Krok KLIKFIX1 (vada V17) — ROZPRACOVANY FORMULAR SE NESMI ZTRATIT POTICHU.
 *
 * Klikaci test 29. 10.: makler vyplnil jmeno klienta a poznamku z hovoru,
 * obnovil stranku (nebo dal „Zpět") a bylo vsechno pryc BEZ VAROVANI
 * (snimek `d-210-nz-prazdne-odeslani.png`). Ulozit rozpracovany formular do
 * prohlizece by znamenalo drzet tam jmeno, telefon a e-mail klienta —
 * osobni udaje mimo server, bez retence; `localStorage` navic smi v `src/**`
 * jen `stores/session.ts` (krok S3). Varovani pred odchodem je proto to, co
 * zadani pripousti („nebo aspoň varování před odchodem").
 *
 * „Vyplneno" se pozna POROVNANIM s vychozim stavem, ne vyctem poli: novy
 * radek formulare se tak do hlidky dostane sam. Zamer a druh nemovitosti
 * jsou predvolby, ne vyplneni — menit je samo o sobe neni rozpracovana prace,
 * proto se do porovnani neberou.
 */
export function wizardHasWork(state: FormState): boolean {
  const empty = initialState();
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  return (Object.keys(empty) as (keyof FormState)[]).some(
    (key) => key !== 'kind' && key !== 'intent' && !same(state[key], empty[key]),
  );
}

const cs = { locale: 'cs' } as const;

// ---- zamer a druh ------------------------------------------------------------

/** Krok UXZ1: zalozeni zna prodej a pronajem; poptavka ma vlastni obrazovku. */
const INTENT_OPTIONS: readonly { readonly value: DealIntent; readonly label: string }[] = [
  { value: 'SALE', label: WIZARD_TEXTS.intentSale },
  { value: 'RENT', label: WIZARD_TEXTS.intentRent },
];

/**
 * Krok UXZ1: kompaktni volby misto velkych ilustrovanych karet (nalez GPT 13).
 * Ilustrace zustavaji tam, kde nesou informaci — v seznamu a detailu zakazky
 * (`CASE_KIND_ILLUSTRATION`).
 */
const TYPE_OPTIONS: readonly { readonly kind: DealKind; readonly label: string }[] = [
  { kind: 'LAND', label: WIZARD_TEXTS.typeLand },
  { kind: 'HOUSE', label: WIZARD_TEXTS.typeHouse },
  { kind: 'APARTMENT', label: WIZARD_TEXTS.typeApartment },
  { kind: 'COMMERCIAL', label: t('webAdmin.wizard.typeCommercial', cs) },
  { kind: 'OTHER', label: WIZARD_TEXTS.typeOther },
];

function Segmented<T extends string>({
  label,
  value,
  options,
  testIdPrefix,
  onChange,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly testIdPrefix: string;
  readonly onChange: (value: T) => void;
}): React.JSX.Element {
  /*
   * Krok UXP4c (P3, nalez „radio skupiny: kazda volba samostatna zastavka
   * Tabu, sipky nic nemeni"): standardni ARIA radiogroup ma JEDNU zastavku
   * Tabu (`tabIndex` jen na vybranem radiu, ostatni -1 — rovnotocny
   * tabindex) a sipky prepinaji vyber i fokus mezi volbami.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = options.findIndex((opt) => opt.value === value);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % options.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const next = options[nextIndex];
    if (next === undefined) return;
    onChange(next.value);
    const nextButton = event.currentTarget.querySelector<HTMLButtonElement>(
      `[data-testid="${testIdPrefix}-${next.value.toLowerCase()}"]`,
    );
    nextButton?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className="rh-view-toggle" onKeyDown={handleKeyDown}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          tabIndex={value === opt.value ? 0 : -1}
          className={`rh-view-toggle__btn${value === opt.value ? ' rh-view-toggle__btn--active' : ''}`}
          data-testid={`${testIdPrefix}-${opt.value.toLowerCase()}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---- klient ------------------------------------------------------------------

type AresState = 'idle' | 'loading' | 'found' | 'not_found' | 'error' | 'invalid';

/**
 * ROZHODNUTI UXZ1-KLIENT: sdilena komponenta vyberu klienta z UXK2 v masteru
 * neni (`components/form-layout.tsx` je jen rozvrzeni), takze klient zustava
 * presne jako dnes — osoba/firma s dohledanim v ARES.
 */
function ClientSection({
  client,
  onChange,
  phoneError,
}: {
  readonly client: DealClient;
  readonly onChange: (client: DealClient) => void;
  /** Zivy pruchod 13. 10. (V6): hláška neplatného telefonu patří K POLI, ne jen do obecného banneru dole. */
  readonly phoneError?: string | undefined;
}): React.JSX.Element {
  const [aresState, setAresState] = React.useState<AresState>('idle');

  const handleAresLookup = async (): Promise<void> => {
    if (!isValidIco(client.ico)) {
      setAresState('invalid');
      return;
    }
    setAresState('loading');
    const outcome = await lookupAres(client.ico);
    if (outcome.status === 'FOUND') {
      onChange({
        ...client,
        companyName: outcome.result.obchodniJmeno,
        registeredAddress: outcome.result.sidloTextovaAdresa,
        aresVerified: true,
      });
      setAresState('found');
    } else if (outcome.status === 'NOT_FOUND') {
      setAresState('not_found');
    } else {
      setAresState('error');
    }
  };

  return (
    <div>
      <div className="rh-view-toggle mb-3" role="group" aria-label={WIZARD_TEXTS.clientLabel}>
        <button
          type="button"
          className={`rh-view-toggle__btn${client.type === 'PERSON' ? ' rh-view-toggle__btn--active' : ''}`}
          aria-pressed={client.type === 'PERSON'}
          data-testid="wizard-client-person"
          onClick={() => onChange({ ...client, type: 'PERSON' })}
        >
          {WIZARD_TEXTS.clientPerson}
        </button>
        <button
          type="button"
          className={`rh-view-toggle__btn${client.type === 'COMPANY' ? ' rh-view-toggle__btn--active' : ''}`}
          aria-pressed={client.type === 'COMPANY'}
          data-testid="wizard-client-company"
          onClick={() => onChange({ ...client, type: 'COMPANY' })}
        >
          {WIZARD_TEXTS.clientCompany}
        </button>
      </div>

      {client.type === 'PERSON' ? (
        <label className="text-sm flex flex-col gap-1">
          {WIZARD_TEXTS.clientPersonNameLabel}
          <input
            type="text"
            className="rh-input"
            value={client.personName}
            onChange={(e) => onChange({ ...client, personName: e.target.value })}
            data-testid="wizard-client-person-name"
          />
        </label>
      ) : null}
      {client.type === 'COMPANY' ? (
        <div className="flex flex-col gap-3">
          <label className="text-sm flex flex-col gap-1">
            {WIZARD_TEXTS.icoLabel}
            <span className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                className="rh-input"
                style={{ flex: 1 }}
                value={client.ico}
                onChange={(e) => { onChange({ ...client, ico: e.target.value, aresVerified: false }); setAresState('idle'); }}
                data-testid="wizard-client-ico"
              />
              <button type="button" className="rh-btn" onClick={() => void handleAresLookup()} data-testid="wizard-ares-lookup">
                {WIZARD_TEXTS.aresButton}
              </button>
            </span>
          </label>
          {aresState !== 'idle' ? (
            <p className="text-sm m-0" data-testid="wizard-ares-status" style={{ color: aresState === 'found' ? 'var(--rh-success)' : aresState === 'loading' ? 'var(--rh-text-muted)' : 'var(--rh-danger)' }}>
              {aresState === 'loading' && WIZARD_TEXTS.aresLoading}
              {aresState === 'found' && `✓ ${WIZARD_TEXTS.aresFound}`}
              {aresState === 'not_found' && WIZARD_TEXTS.aresNotFound}
              {aresState === 'error' && WIZARD_TEXTS.aresError}
              {aresState === 'invalid' && WIZARD_TEXTS.aresInvalidIco}
            </p>
          ) : null}
          <label className="text-sm flex flex-col gap-1">
            {WIZARD_TEXTS.companyNameLabel}
            <input
              type="text"
              className="rh-input"
              value={client.companyName}
              onChange={(e) => onChange({ ...client, companyName: e.target.value, aresVerified: false })}
              data-testid="wizard-client-company-name"
            />
          </label>
          <label className="text-sm flex flex-col gap-1">
            {WIZARD_TEXTS.companyAddressLabel}
            <input
              type="text"
              className="rh-input"
              value={client.registeredAddress}
              onChange={(e) => onChange({ ...client, registeredAddress: e.target.value, aresVerified: false })}
              data-testid="wizard-client-company-address"
            />
            {client.aresVerified ? (
              <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>{WIZARD_TEXTS.companyAddressAresHint}</span>
            ) : null}
          </label>
        </div>
      ) : null}

      {/*
        Krok UXP1 (nalez P1-1): kontakt klienta. Bez nej mel makler klienta
        zalozeneho bez jedine cesty, jak mu poslat smlouvu k podpisu —
        magic link si e-mail vyzadal znovu.
      */}
      <FieldGrid className="mt-3">
        <label className="text-sm flex flex-col gap-1">
          {t('webAdmin.wizard.clientEmailLabel', cs)}
          <input
            type="email"
            className="rh-input"
            value={client.email}
            onChange={(e) => onChange({ ...client, email: e.target.value })}
            data-testid="wizard-client-email"
          />
        </label>
        <label className="text-sm flex flex-col gap-1">
          {t('webAdmin.wizard.clientPhoneLabel', cs)}
          <input
            type="tel"
            className="rh-input"
            value={client.phone}
            onChange={(e) => onChange({ ...client, phone: e.target.value })}
            data-testid="wizard-client-phone"
          />
          {phoneError !== undefined && (
            <p className="rh-a2-error m-0" role="alert" data-testid="wizard-client-phone-error">
              {phoneError}
            </p>
          )}
        </label>
      </FieldGrid>
    </div>
  );
}

// ---- misto ------------------------------------------------------------------

/**
 * Krok M1 — klik do mapy: bod (WGS84 z Leaflet preview) -> S-JTSK -> K0c
 * resolver (`GET .../map/parcel-at-point`) -> navrzena identita -> uzivatel
 * potvrdi -> stejna pole jako pri rucnim zadani (`parcelNumber`/
 * `cadastralArea`). Jedno kliknuti = jedna navrzena parcela.
 */
type ParcelPickState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | {
      readonly kind: 'proposed';
      readonly label: string;
      readonly cadastralArea: string;
      /**
       * Krok PRF3 bod 1 a 2: identita parcely z RUIAN. `ruianId` je klic
       * k sousednim pozemkum (`map/parcel-neighbours`), `cadastralAreaCode`
       * je to, co dela z „597" jednoznacnou parcelu (`parcels_area_ref_uq`).
       */
      readonly ruianId: string;
      readonly cadastralAreaCode: string | null;
    }
  | { readonly kind: 'no_parcel' }
  | { readonly kind: 'error'; readonly reason: string };

/**
 * Krok GRAF5 — PANEL „CO VIME Z UREDNICH ZDROJU" (predloha
 * `/srv/debata/maklerov-grafika-formular/VYSLEDEK.png`, vpravo).
 *
 * Nic NENACITA: cte udaje, ktere uz formular ma — adresu potvrzenou bodem
 * z naseptavace nebo klikem do mapy, katastralni uzemi a parcelu. To je cely
 * rozdil mezi „Ověřeno" a „K doplnění": OVERENE je to, co prislo z uredniho
 * zdroje (RUIAN / katastr), ne to, co clovek napsal rukou. Rucne napsana
 * parcela proto stitek „Ověřeno" NEDOSTANE — tvrdilo by to o udaji neco,
 * co nikdo nepotvrdil (tataz uvaha jako `wizard-address-unconfirmed`).
 */
function OfficialSourcesPanel({
  rows,
}: {
  readonly rows: readonly { readonly label: string; readonly value: string; readonly verified: boolean }[];
}): React.JSX.Element {
  return (
    <section className="rh-card rh-wiz-sources" data-testid="wizard-sources">
      <h2 style={{ margin: '0 0 8px', fontSize: 'var(--rh-size-section)' }}>
        {t('webAdmin.wizard.sourcesTitle', cs)}
      </h2>
      <ul className="rh-wiz-sources__list">
        {rows.map((row) => (
          <li key={row.label} data-testid={`wizard-source-${row.verified ? 'ok' : 'missing'}`}>
            <span className="rh-wiz-sources__label">{row.label}</span>
            <span className="rh-wiz-sources__value">{row.value === '' ? '—' : row.value}</span>
            <span className={`rh-a2-badge ${row.verified ? 'rh-a2-badge--ok' : 'rh-a2-badge--wait'}`}>
              {row.verified
                ? t('webAdmin.wizard.sourcesVerified', cs)
                : t('webAdmin.wizard.sourcesMissing', cs)}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-sm m-0 mt-3" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-sources-note">
        {t('webAdmin.wizard.sourcesNote', cs)}
      </p>
    </section>
  );
}

/** Parcela predmetu tak, jak ji vratil RUIAN — ne to, co clovek napsal rukou. */
interface ResolvedParcel {
  readonly ruianId: string;
  readonly label: string;
  readonly cadastralAreaCode: string | null;
}

function ParcelPickStatus({
  pick,
  onConfirm,
}: {
  readonly pick: ParcelPickState;
  readonly onConfirm: () => void;
}): React.JSX.Element | null {
  if (pick.kind === 'idle') return null;
  if (pick.kind === 'pending') {
    return (
      <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-parcel-pick-pending">
        {t('webAdmin.parcelMap.click.pending', cs)}
      </p>
    );
  }
  if (pick.kind === 'no_parcel') {
    return (
      <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-parcel-pick-no-parcel">
        {t('webAdmin.parcelMap.click.noParcel', cs)}
      </p>
    );
  }
  if (pick.kind === 'error') {
    return (
      <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-danger)' }} data-testid="wizard-parcel-pick-error">
        {t('webAdmin.parcelMap.click.error', { ...cs, values: { reason: pick.reason } })}
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2 mt-2" data-testid="wizard-parcel-pick-proposed">
      <p className="text-sm m-0">
        {t('webAdmin.parcelMap.click.proposed', {
          ...cs,
          values: { label: pick.label, cadastralArea: pick.cadastralArea },
        })}
      </p>
      <button type="button" className="rh-btn" onClick={onConfirm} data-testid="wizard-parcel-pick-confirm">
        {t('webAdmin.parcelMap.click.confirm', cs)}
      </button>
    </div>
  );
}

function parcelPickFromLookup(result: ParcelLookupDto): ParcelPickState {
  if (result.parcel === null) return { kind: 'no_parcel' };
  const code = result.parcel.cadastralAreaCode ?? result.cadastralArea?.code ?? null;
  return {
    kind: 'proposed',
    label: result.parcel.label,
    cadastralArea: result.cadastralArea?.name ?? '',
    ruianId: result.parcel.id,
    cadastralAreaCode: code === null ? null : String(code),
  };
}

/**
 * NABIDKA POZEMKU DOMU (krok PRF3 bod 2).
 *
 * Zivy pruchod 13. 10. 2026: dum c.p. 475 v Petrovicich se zalozil jen se
 * STAVEBNI parcelou st. 597, takze report tvrdil „vymera pozemku 176 m²"
 * o cele nemovitosti, zatimco pozemky domu (1904/12, 1907/23 — dohromady
 * 1 075 m²) v zakazce vubec nebyly. Sousedy vydava server z RUIAN; co
 * k domu patri, rozhoduje ZASKRTNUTI maklerem — vlastnictvi v otevrenych
 * datech neni (KN4), takze tise pridany soused by byl lez o predmetu.
 */
type HouseLandState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly items: HouseLandNeighboursDto['items'] }
  | { readonly kind: 'unavailable' };

/**
 * Radek nabidky: parcela, druh pozemku a vymera z RUIAN — presne to, podle
 * ceho makler pozna, jestli pozemek k domu patri. Vymera chybi u parcely,
 * u ktere ji RUIAN nevede; „zadna vymera" se neprodava jako nula.
 */
function houseLandOptionText(item: HouseLandNeighboursDto['items'][number]): string {
  const landType = item.landType ?? item.landUse ?? t('webAdmin.wizard.houseLandUnknownType', cs);
  if (item.areaKnM2 === null) {
    return t('webAdmin.wizard.houseLandOptionNoArea', { ...cs, values: { label: item.label, landType } });
  }
  return t('webAdmin.wizard.houseLandOption', {
    ...cs,
    values: { label: item.label, landType, area: item.areaKnM2.toLocaleString('cs-CZ') },
  });
}

/**
 * Krok B1 — stav nacitani inzeratu. `manual` NENI chyba: je to poctiva
 * odpoved u portalu, ktery strojove cteni zakazuje v robots.txt (Sreality).
 * Zakaz se neobchazi - makler(ka) si inzerat otevre sama a udaje opise.
 */
type ListingState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded' }
  | { readonly kind: 'manual'; readonly reason: string; readonly humanAccess: string | null }
  | { readonly kind: 'error' };

function ListingStatus({ state }: { readonly state: ListingState }): React.JSX.Element | null {
  if (state.kind === 'idle') return null;
  const tone =
    state.kind === 'loaded' ? 'var(--rh-success)' : state.kind === 'loading' ? 'var(--rh-text-muted)' : 'var(--rh-danger)';
  return (
    <p className="text-sm m-0 mt-2" style={{ color: tone }} data-testid={`wizard-listing-${state.kind}`}>
      {state.kind === 'loading' && WIZARD_TEXTS.listingLoading}
      {state.kind === 'loaded' && WIZARD_TEXTS.listingLoaded}
      {state.kind === 'manual' && t('webAdmin.wizard.listingManual', { ...cs, values: { reason: state.reason } })}
      {state.kind === 'error' && WIZARD_TEXTS.listingError}
    </p>
  );
}

/**
 * Krok GPS1 — „Moje poloha“ na mobilu maklera: `getCurrentPosition`
 * s `enableHighAccuracy`, jeden bod (ne `watchPosition`). Presnost se
 * NEVYNUCUJE do 30 m — bod prijmeme vzdy, pri slabem signalu rekneme, at ho
 * makler dojede kliknutim do mapy.
 */
interface LocatedPosition {
  readonly lat: number;
  readonly lon: number;
  /** Polomer kruhu 95 % v metrech — to, co prohlizec vraci. */
  readonly accuracy: number;
}

function locationNoticeText(entry: LocationEntry | null): string | null {
  if (entry === null) return null;
  if (entry.kind === 'error') {
    return t(`webAdmin.wizard.location.error.${entry.code}` as Parameters<typeof t>[0], cs);
  }
  if (entry.kind === 'unavailable') return t('webAdmin.wizard.location.shortLinkFailed', cs);
  if (entry.kind === 'expanding') return t('webAdmin.wizard.location.shortLink', cs);
  return null;
}

// ---- stranka ----------------------------------------------------------------

type CreateState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'error'; readonly message: string }
  /** Krok PRF3 bod 1: parcelu uz kancelar vede u jine nemovitosti (409). */
  | { readonly kind: 'parcels_already_used'; readonly propertyIds: readonly string[] }
  /**
   * Krok P8F1 bod 1b: na teze parcele uz karta je, ale makler cislo jednotky
   * nezadal. Bez jeho odpovedi se karta NESPOJI ani NEROZDELI — tise spojit
   * dva byty je vada P8-N1, tise rozdelit zalozi duplikat.
   */
  | { readonly kind: 'same_property_ask'; readonly candidate: SamePropertyCandidate }
  /**
   * Krok P8F6 bod 2: e-mail z formulare uz v kancelari nekdo ma. Vybira
   * MAKLER — nic se neslucuje samo (shoda oponentury 27. 10.).
   */
  | { readonly kind: 'existing_client_ask'; readonly party: ExistingClient }
  /**
   * Krok P8F6 bod 1: zakazka JE zalozena, ale nektery udaj se na kartu
   * nemovitosti nezapsal. Makler vidi, KTERY udaj to je, a muze to zkusit
   * znovu — opakovani jde pod tymz klicem odeslani, takze druha zakazka
   * nevznikne.
   */
  | {
      readonly kind: 'facts_failed';
      readonly caseId: string;
      readonly factKeys: readonly string[];
    };

/** Klient z adresare, ktery uz ma tentyz e-mail (krok P8F6 bod 2). */
interface ExistingClient {
  readonly partyId: string;
  readonly displayName: string;
  readonly kind: 'person' | 'organization';
}

/** Zivy pruchod 13. 10. (V6): typ serverove chyby, kterou patri zobrazit U POLE, ne jen jako obecny banner. */
const PHONE_ERROR_TYPE = 'party/invalid-phone';

export function NovaZakazkaPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = React.useState<FormState>(() => initialState());
  const [parcelPick, setParcelPick] = React.useState<ParcelPickState>({ kind: 'idle' });
  /** Krok UXP4a bod 4: parcela a k. u. prisly z katastru podle adresy. */
  const [cadastralFromAddress, setCadastralFromAddress] = React.useState(false);
  /** Krok PRF3 bod 1 a 2: identita parcely predmetu z RUIAN (po potvrzeni). */
  const [resolvedParcel, setResolvedParcel] = React.useState<ResolvedParcel | null>(null);
  const [houseLand, setHouseLand] = React.useState<HouseLandState>({ kind: 'idle' });
  const [houseLandPicked, setHouseLandPicked] = React.useState<readonly string[]>([]);
  const [listing, setListing] = React.useState<ListingState>({ kind: 'idle' });
  const [coordinateText, setCoordinateText] = React.useState('');
  const [locationNotice, setLocationNotice] = React.useState<LocationEntry | null>(null);
  const [locateState, setLocateState] = React.useState<LocateFailure | 'locating' | null>(null);
  const [located, setLocated] = React.useState<LocatedPosition | null>(null);
  const [create, setCreate] = React.useState<CreateState>({ kind: 'idle' });
  const [phoneError, setPhoneError] = React.useState<string | undefined>(undefined);

  /** Odpovedny makler = prihlaseny uzivatel (zakazku zaklada on, `cases.owner_user_id`). */
  const brokerName = useSession((s) => s.user?.name ?? null);

  /**
   * Krok GPS1 — poloha z odkazu `zakazky/novy?lat=…&lon=…`. `label` tu
   * ZAMERNE NENI: pod popiskem lezi parcelni cislo, ktere by slo schovat do
   * odkazu — bere se jen bod a i ten jde do NAVRHU parcely k potvrzeni.
   */
  const rawSearch = zakazkyNovyRouteApi.useSearch();
  const initialLocation = React.useMemo(() => parsePlaceLink(rawSearch)?.point ?? null, [rawSearch]);

  const patch = (p: Partial<FormState>): void => setState((prev) => ({ ...prev, ...p }));

  /*
   * Krok KLIKFIX1 (vada V17): obnoveni stranky ani zavreni zalozky nesmi
   * rozpracovany formular zahodit bez slova. Jakmile zakazka VZNIKLA
   * (`facts_failed` — zalozena, jen se nezapsal udaj na kartu) uz hlidka
   * nebezi: formular neni co zachranovat.
   *
   * Krok APP2 bod 4: samotna hlidka zije v `lib/leave-guard.ts` — tytez
   * radky potrebuje „Nová faktura" i rozepsany text inzeratu a tri kopie
   * `beforeunload` by se rozesly.
   */
  useLeaveWarning(create.kind !== 'facts_failed' && wizardHasWork(state));

  /**
   * Parcela pod bodem — JEDNA cesta k resolveru (`GET …/map/parcel-at-point`)
   * pro klik do mapy i pro vyber adresy z naseptavace.
   */
  const lookupParcelAt = (lat: number, lon: number, apply: (pick: ParcelPickState) => void): void => {
    setParcelPick({ kind: 'pending' });
    const point = wgs84ToSjtsk({ lat, lon });
    fetchParcelAtPoint(point.x, point.y)
      .then((result) => { apply(parcelPickFromLookup(result)); })
      .catch((error: unknown) => {
        setParcelPick({ kind: 'error', reason: error instanceof Error ? error.message : String(error) });
      });
  };

  const handleMapPick = (lat: number, lon: number, accuracy: number | null = null): void => {
    setState((prev) => ({ ...prev, address: { ...prev.address, lat, lon, source: 'manual' } }));
    setLocated(accuracy === null ? null : { lat, lon, accuracy });
    // Klik do mapy zustava NAVRHEM k potvrzeni: bod si vybral clovek rukou a
    // muze minout (ROZHODNUTI GPS1 — „jedno kliknuti = jedna navrzena parcela").
    lookupParcelAt(lat, lon, setParcelPick);
  };

  /**
   * Krok UXP4a bod 4 (nalez P2-17) — VYBER ADRESY Z NASEPTAVACE. Dnesek slibuje
   * „Stačí adresa — katastr a údaje o domě se doplní samy", ale „Parcelní číslo"
   * a „Katastrální území" zustavaly po vyberu adresy prazdne.
   *
   * ODLISNOST OD KLIKU DO MAPY JE ZAMERNA: adresni bod je z RUIAN, ne z ruky
   * uzivatele, takze se pole DOPLNI a jen se u nich rekne, odkud to je — pole
   * zustavaji editovatelna. Resolver je tentyz (`lookupParcelAt`).
   */
  const handleAddressChange = (address: DealAddress): void => {
    const moved = address.lat !== state.address.lat || address.lon !== state.address.lon;
    patch({ address });
    if (!moved || address.lat === null || address.lon === null) return;
    setCadastralFromAddress(false);
    lookupParcelAt(address.lat, address.lon, (pick) => {
      if (pick.kind !== 'proposed') {
        setParcelPick(pick);
        return;
      }
      patch({ parcelNumber: pick.label, cadastralArea: pick.cadastralArea });
      setParcelPick({ kind: 'idle' });
      setCadastralFromAddress(true);
      setResolvedParcel({ ruianId: pick.ruianId, label: pick.label, cadastralAreaCode: pick.cadastralAreaCode });
    });
  };

  const handleConfirmParcel = (): void => {
    if (parcelPick.kind !== 'proposed') return;
    patch({ parcelNumber: parcelPick.label, cadastralArea: parcelPick.cadastralArea });
    setParcelPick({ kind: 'idle' });
    setResolvedParcel({
      ruianId: parcelPick.ruianId,
      label: parcelPick.label,
      cadastralAreaCode: parcelPick.cadastralAreaCode,
    });
  };

  /**
   * Rucni prepis parcelniho cisla nebo k. u. ZAHAZUJE identitu z RUIAN:
   * cislo k. u. a sousedy uz nepatri k tomu, co je v poli (krok PRF3 bod 1 —
   * „597" bez uzemi je jina nemovitost v kazdem katastralnim uzemi).
   */
  const forgetResolvedParcel = (): void => {
    setResolvedParcel(null);
    setHouseLand({ kind: 'idle' });
    setHouseLandPicked([]);
  };

  /** Krok GPS1 — „Moje poloha“ (telefon na pozemku). */
  const handleLocate = (): void => {
    const geolocation = globalThis.navigator?.geolocation;
    if (geolocation === undefined) {
      setLocateState('unsupported');
      return;
    }
    setLocateState('locating');
    setLocated(null);
    geolocation.getCurrentPosition(
      (position) => {
        setLocateState(null);
        setLocationNotice(null);
        handleMapPick(position.coords.latitude, position.coords.longitude, position.coords.accuracy);
      },
      (error) => { setLocateState(locateFailureOf(error)); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 },
    );
  };

  /**
   * Krok GPS1 — souradnice textem nebo odkaz z mapy. Kratky odkaz se rozbija
   * az na serveru (`fetchMapLink`); rozbaleni v prohlizeci by sahalo na cizi
   * host zpusobem, ktery nejde vypnout v CSP.
   */
  const applyCoordinateText = (): void => {
    const entry = classifyLocationEntry(coordinateText);
    if (entry.kind === 'point') {
      setLocationNotice(null);
      handleMapPick(entry.lat, entry.lon, entry.accuracy);
      return;
    }
    if (entry.kind === 'error' || entry.kind === 'unavailable') {
      setLocationNotice(entry);
      return;
    }
    if (entry.kind === 'ignored') {
      setLocationNotice(null);
      return;
    }
    setLocationNotice(entry);
    void expandLocationLink(coordinateText.trim()).then((resolved) => {
      setLocationNotice(resolved.kind === 'point' ? null : resolved);
      if (resolved.kind === 'point') handleMapPick(resolved.lat, resolved.lon, null);
    });
  };

  /**
   * Krok B1 — inzerat formular jen PREDVYPLNI. Prepisuji se proto jen pole,
   * ktera jsou prazdna: co uz clovek vyplnil, ma prednost pred inzerentem.
   */
  const handleListingLoad = async (): Promise<void> => {
    const url = state.listingUrl.trim();
    if (url === '') return;
    setListing({ kind: 'loading' });
    try {
      const result = await fetchListingPrefill(url);
      if (result.status !== 'OK' || result.prefill === null) {
        setListing({ kind: 'manual', reason: result.reasonCode ?? 'UNKNOWN', humanAccess: result.humanAccess });
        return;
      }
      const address = result.prefill.address;
      if (address !== null && state.address.query === '') {
        patch({ address: { ...state.address, query: address, source: 'manual' } });
      }
      setListing({ kind: 'loaded' });
    } catch {
      setListing({ kind: 'error' });
    }
  };

  const prefillApplied = React.useRef(false);
  React.useEffect(() => {
    if (initialLocation === null || prefillApplied.current) return;
    prefillApplied.current = true;
    handleMapPick(initialLocation.lat, initialLocation.lon, null);
  }, [initialLocation]);

  /**
   * Krok PRF3 bod 2 — sousedni pozemky se nactou teprve u DOMU a teprve
   * k parcele potvrzene z RUIAN. U pozemku a bytu by to byla nabidka bez
   * otazky: pozemek se zaklada na parcele, kterou makler zada, a byt na
   * jednotce. Prepnuti druhu po vyberu parcely nabidku dohledava taky,
   * proto efekt a ne jedno volani ve `handleConfirmParcel`.
   */
  const resolvedParcelId = resolvedParcel?.ruianId ?? null;
  const wantsHouseLand = state.kind === 'HOUSE' && resolvedParcelId !== null;
  React.useEffect(() => {
    if (!wantsHouseLand || resolvedParcelId === null) {
      setHouseLand({ kind: 'idle' });
      return;
    }
    let active = true;
    setHouseLand({ kind: 'loading' });
    fetchParcelNeighbours(resolvedParcelId)
      .then((result) => {
        if (!active) return;
        setHouseLand(
          result.unavailableReason === null
            ? { kind: 'ready', items: result.items }
            : { kind: 'unavailable' },
        );
      })
      .catch(() => {
        if (active) setHouseLand({ kind: 'unavailable' });
      });
    return () => {
      active = false;
    };
  }, [wantsHouseLand, resolvedParcelId]);

  /**
   * ZALOZENI ZAKAZKY. Zadne provereni — to je dalsi krok z detailu
   * (zadani UXZ1 bod 2). Identita (parcela, katastralni uzemi, LV, jednotka)
   * zustava ve formulari a makler ji doplnuje v sekci Nemovitost; sem jde
   * nazev z adresy, bod a popis, protoze jine pole zakazka pri zalozeni nema.
   */
  const identityLine = (): string => {
    const parts = [
      state.parcelNumber === '' ? null : `${WIZARD_TEXTS.parcelNumberLabel}: ${state.parcelNumber}`,
      state.cadastralArea === '' ? null : `${WIZARD_TEXTS.cadastralAreaLabel}: ${state.cadastralArea}`,
      state.landRegistryNumber === '' ? null : `${WIZARD_TEXTS.landRegistryLabel}: ${state.landRegistryNumber}`,
      state.unitNumber === '' ? null : `${WIZARD_TEXTS.unitNumberLabel}: ${state.unitNumber}`,
      state.layout === '' ? null : `${WIZARD_TEXTS.layoutLabel}: ${state.layout}`,
    ].filter((part): part is string => part !== null);
    return parts.join(', ');
  };

  /*
   * Krok UXD4 (nalez V4 UX auditu 21. 10.) — POPIS MA DVE CASTI, NE TRI RADKY.
   *
   * Prvni radek je SOUHRN, ktery slozila appka (cena a identita nemovitosti);
   * druhy je „Zadání makléře", tedy jeho vlastni veta. Seznam zakazek kresli
   * pod adresou jen prvni radek (`caseSummaryLine` v `lib/case-rows.ts`),
   * takze souhrn musi byt cely na nem — do UXD4 stala cena na svem radku a
   * do sloupce Adresa se slila i poznamka.
   *
   * Cena se formatuje `formatMoney` z TEHOZ cisla, ktere jde do zakazky jako
   * predstava majitele (`parseMoneyInput`) — jinak v seznamu stoji „Cenová
   * představa: 990000" vedle radku s „4 900 000 Kč".
   */
  const summaryLine = (): string => {
    const price = parseMoneyInput(state.priceExpectation);
    /*
     * NEZLOMITELNA MEZERA PATRI DO SAZBY, NE DO ULOZENEHO TEXTU.
     * `formatMoney` (a `Intl`) oddeluje tisice NBSP (U+00A0) — spravne,
     * protoze „5 900" se v sazbe nesmi zalomit. `cases.description` je ale
     * ULOZENY PLAIN TEXT, nad kterym jede hledani v seznamu zakazek: makléř
     * do hledani napise obycejnou mezeru a NBSP by mu radek nenasel. Do
     * ulozeneho textu proto jde obycejna mezera; sazba si NBSP doplni sama
     * tam, kde cislo skutecne formatuje (`formatCzechNumber`, `formatMoney`).
     */
    const formatted = (price === null ? null : formatMoney(price)?.replace(/[\u00a0\u202f]/g, ' ')) ?? null;
    return [
      formatted === null
        ? null
        : t('webAdmin.wizard.priceExpectationNote', { ...cs, values: { value: formatted } }),
      identityLine() === '' ? null : identityLine(),
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
  };

  const description = (): string =>
    [summaryLine() === '' ? null : summaryLine(), state.notes.trim() === '' ? null : state.notes.trim()]
      .filter((line): line is string => line !== null)
      .join('\n');

  /**
   * Krok UXP1 (nalez P1-2): hlaska slibuje „adresu NEBO parcelu", takze
   * pozemek jde zalozit i jen podle parcelniho cisla a katastralniho uzemi.
   * Nazev je pak `parc. č. X, k. ú. Y` — presne to, pod cim ho makler hleda.
   */
  const caseTitle = (): string => {
    const address = state.address.query.trim();
    if (address !== '') return address;
    const parcel = state.parcelNumber.trim();
    if (parcel === '') return '';
    const area = state.cadastralArea.trim();
    return t(area === '' ? 'webAdmin.wizard.titleFromParcel' : 'webAdmin.wizard.titleFromParcelInArea', {
      ...cs,
      values: { parcel, area },
    });
  };

  /** Klient z formulare jako STRANA zakazky (nalez P1-1), ne text v popisu. */
  const clientInput = (): CaseClientInput | null => {
    const displayName = (state.client.type === 'PERSON' ? state.client.personName : state.client.companyName).trim();
    if (displayName === '') return null;
    return {
      kind: state.client.type === 'PERSON' ? 'person' : 'organization',
      displayName,
      contacts: [
        { kind: 'email' as const, value: state.client.email },
        { kind: 'phone' as const, value: state.client.phone },
      ].filter((contact) => contact.value.trim() !== ''),
      role: CLIENT_ROLE_BY_DEAL_INTENT[state.intent],
    };
  };

  /**
   * PARCELY PREDMETU (krok PRF3 body 1 a 2).
   *
   * Prvni je parcela z formulare (u domu stavebni), za ni ZASKRTNUTE pozemky
   * domu. `cadastralAreaCode` jde s kazdou parcelou, ktera prisla z RUIAN —
   * az s nim je „597" jednoznacna parcela (`parcels_area_ref_uq`); u rucne
   * napsaneho cisla ho nemame a nevymyslime ho.
   */
  const parcelsForCreate = (): readonly NewPropertyParcel[] => {
    const parcelRef = state.parcelNumber.trim();
    if (parcelRef === '') return [];
    const resolvedCode =
      resolvedParcel !== null && resolvedParcel.label === parcelRef ? resolvedParcel.cadastralAreaCode : null;
    const main: NewPropertyParcel = {
      jurisdiction: 'CZ',
      externalRef: parcelRef,
      ...(resolvedCode === null ? {} : { cadastralAreaCode: resolvedCode }),
    };
    const picked = houseLand.kind !== 'ready' ? [] : houseLand.items.filter((item) => houseLandPicked.includes(item.ruianId));
    return [
      main,
      ...picked.map((item) => ({
        jurisdiction: 'CZ',
        externalRef: item.label,
        ...(item.cadastralAreaCode === null ? {} : { cadastralAreaCode: item.cadastralAreaCode }),
      })),
    ];
  };

  /**
   * Krok P8F1 bod 2 — KLIC JEDNOHO ODESLANI PRUVODCE.
   *
   * Zustava stejny pro dvojklik, pro opakovani po sitove chybe i pro
   * opakovani s odpovedi „Jde o tentyz byt?", protoze to vsechno je JEDNO
   * zalozeni. Nulu dostane az po uspechu — dalsi zakazka je jine odeslani.
   */
  const submissionKey = React.useRef<string | null>(null);

  /**
   * Krok P8F6 bod 2 — ROZHODNUTI MAKLERE O KLIENTOVI.
   *
   * `null` = jeste jsme se neptali; `{ partyId: null }` = makler rekl „založit
   * nového"; `{ partyId: '…' }` = pouzij klienta z adresare. Drzi se po celou
   * dobu jednoho zalozeni (vcetne opakovani), aby se otazka nevracela.
   */
  const clientChoice = React.useRef<{ readonly partyId: string | null } | null>(null);

  /**
   * Krok P8F6 bod 1 — UDAJE Z PRUVODCE JAKO FAKTA KARTY.
   *
   * Dispozice, cislo jednotky a katastralni uzemi slo do P8F6 JEN do textu
   * popisu zakazky (`identityLine`), takze se na nabor makler musel ptat
   * znovu (nalez P8-N7). Klice jsou z registru `FACT_KEYS` a jsou TYTEZ,
   * jake cte checklist naboru (`field-intake/templates/*.json`) — proto se
   * po zalozeni ukazou jako „Už víme: …".
   *
   * Puvod je MAKLER (`sourceType: 'user'`, status jiny nez `VERIFIED`), stejne
   * jako u „Uložit i do zakázky" ze smlouvy (`properties.ts`
   * `savePropertyFactFromContract`) — ne tvrzeni majitele, protoze tyhle tri
   * udaje makler opisuje z katastru a z inzeratu, ne z vety majitele.
   */
  const factsFromWizard = (): readonly CaseFactInput[] => {
    const broker = { status: 'UNKNOWN', sourceType: 'user' } as const;
    return (
      [
        ['disposition', state.layout.trim()],
        ['unit_number', state.unitNumber.trim()],
        ['cadastral_territory', state.cadastralArea.trim()],
      ] as const
    )
      .filter(([, value]) => value !== '')
      .map(([factKey, value]) => ({ factKey, value, provenance: broker }));
  };

  const handleCreate = (decision?: { readonly propertyId: string; readonly same: boolean }): void => {
    const title = caseTitle();
    /*
     * Krok PRF1 (vada V1) — PARCELA BEZ KATASTRALNIHO UZEMI SE NEPOSILA.
     * „38/13" je v kazdem katastralnim uzemi JINA nemovitost; bez uzemi by
     * predmet urcila adresa, a presne tim zakazka 13. 10. skoncila na cizi
     * parcele v jine obci. Chyba stoji U POLE, ne jen vedle tlacitka (UXP2).
     */
    if (state.parcelNumber.trim() !== '' && state.cadastralArea.trim() === '') {
      setCreate({ kind: 'error', message: t('webAdmin.wizard.parcelNeedsArea', cs) });
      focusField('wizard-cadastral-area');
      return;
    }
    if (title === '') {
      setCreate({ kind: 'error', message: t('webAdmin.wizard.addressRequired', cs) });
      // Krok UXP2 (P2-4): na mobilu stala chyba jen vedle tlacitka dole a
      // pole Adresa bylo mimo obrazovku bez zvyrazneni a bez fokusu.
      focusField('wizard-address-input');
      return;
    }
    setCreate({ kind: 'submitting' });
    setPhoneError(undefined);
    /*
     * Krok P8F6 bod 2 — E-MAIL, KTERY V KANCELARI UZ NEKDO MA.
     * Ptame se JEN RAZ za jedno zalozeni a jen kdyz je e-mail vyplneny;
     * selhani dotazu zalozeni nezdrzi (makler dostane novou stranu jako
     * dosud, duplikat se da slouspit pres `merge-suggestions`).
     */
    const email = state.client.email.trim();
    if (clientChoice.current === null && email !== '' && clientInput() !== null) {
      void findPartyByEmail(email).then(
        (party) => {
          if (party === null) {
            clientChoice.current = { partyId: null };
            submitCase(decision);
            return;
          }
          setCreate({
            kind: 'existing_client_ask',
            party: { partyId: party.id, displayName: party.displayName, kind: party.kind },
          });
        },
        () => {
          clientChoice.current = { partyId: null };
          submitCase(decision);
        },
      );
      return;
    }
    submitCase(decision);
  };

  /** Samotne zalozeni (karta -> zakazka -> fakta). Klic odeslani je JEDEN. */
  const submitCase = (decision?: { readonly propertyId: string; readonly same: boolean }): void => {
    const title = caseTitle();
    const unitLabel = state.unitNumber.trim();
    const chosenPartyId = clientChoice.current?.partyId ?? null;
    submissionKey.current ??= crypto.randomUUID();
    void createCaseOverProperty({
      submissionKey: submissionKey.current,
      ...(decision === undefined ? {} : { samePropertyDecision: decision }),
      title,
      addressQuery: state.address.query.trim(),
      propertyKind: PROPERTY_KIND_BY_DEAL_KIND[state.kind],
      caseType: CASE_TYPE_BY_DEAL_INTENT[state.intent],
      location:
        state.address.lat !== null && state.address.lon !== null
          ? { lat: state.address.lat, lon: state.address.lon }
          : null,
      description: description(),
      client: clientInput(),
      ...(chosenPartyId === null ? {} : { clientPartyId: chosenPartyId }),
      ...(unitLabel === '' ? {} : { units: [{ label: unitLabel, kind: 'apartment' as const }] }),
      ...(parcelsForCreate().length === 0 ? {} : { parcels: parcelsForCreate() }),
      // Krok UXP5 bod 2: cenova predstava jde I jako fakt nemovitosti, aby ji
      // inzerat a zaklad provize nemusel makler psat podruhe.
      askingPrice: parseMoneyInput(state.priceExpectation),
      // Krok PROP3 bod 3: bez zaskrtnuti jde cislo jen jako predstava majitele.
      priceIsAsking: state.priceIsAsking,
      // Krok P8F6 bod 1: dispozice, jednotka a k. u. na kartu nemovitosti.
      facts: factsFromWizard(),
    })
      .then((result) => {
        /*
         * Krok P8F6 bod 1: kdyz se udaj na kartu nezapsal, makler to VIDI.
         * Do P8F6 se chyba polykala (`catch {}` v `case-create.ts`) a udaj
         * se ztratil bez stopy. Zakazka uz existuje, takze se na ni da jit
         * i bez opakovani.
         */
        if (result.factsFailed.length > 0) {
          setCreate({ kind: 'facts_failed', caseId: result.caseId, factKeys: result.factsFailed });
          return;
        }
        void navigate({ to: '/zakazky/$id', params: { id: result.caseId } });
      })
      .catch((error: unknown) => {
        // Zivy pruchod 13. 10. (V6): neplatny telefon je chyba POLE klienta,
        // ne obecneho formulare — hlaska patri pod pole „Telefon", ne jen do
        // banneru dole, kam se dosud cpala jako „reason".
        if (error instanceof DomainApiError && error.type !== undefined && kodChyby(error.type) === PHONE_ERROR_TYPE) {
          setCreate({ kind: 'idle' });
          setPhoneError(error.message);
          focusField('wizard-client-phone');
          return;
        }
        /*
         * Krok PRF3 bod 1 — PARCELA, KTEROU KANCELAR UZ MA, JE KONFLIKT STAVU.
         * Server vraci 409 `property/parcels-already-used` a v `detail` nese
         * ID existujicich nemovitosti pro odkaz. `detail` je ale to, co
         * `unwrap` bere jako `message`, takze bez tehle vetve by makler
         * v banneru cetl „propertyIds=01a1…". Veta je z katalogu, odkaz
         * vede na existujici nemovitost.
         */
        const alreadyUsed = parcelsAlreadyUsedPropertyIds(error);
        if (alreadyUsed !== null) {
          setCreate({ kind: 'parcels_already_used', propertyIds: alreadyUsed });
          focusField('wizard-parcel-number');
          return;
        }
        /*
         * Krok P8F1 bod 1b — NA TEZE PARCELE UZ KARTA JE A CISLO JEDNOTKY
         * CHYBI. Server se pta (409) a posila adresu, jednotku i dispozici
         * nalezene karty, aby makler poznal, jestli je to tyz byt. Dokud
         * neodpovi, nevznikla ani karta, ani zakazka.
         */
        const candidate = samePropertyCandidate(error);
        if (candidate !== null) {
          setCreate({ kind: 'same_property_ask', candidate });
          focusField('wizard-unit-number');
          return;
        }
        setCreate({
          kind: 'error',
          message: t('webAdmin.wizard.createFailed', {
            ...cs,
            values: { reason: error instanceof Error ? error.message : String(error) },
          }),
        });
      });
  };

  const houseLandVisible = state.kind === 'HOUSE' && houseLand.kind !== 'idle';

  const toggleHouseLand = (ruianId: string): void => {
    setHouseLandPicked((prev) =>
      prev.includes(ruianId) ? prev.filter((id) => id !== ruianId) : [...prev, ruianId],
    );
  };

  const hasPlace = state.address.lat !== null && state.address.lon !== null;
  /**
   * Krok PRF1 (vada V1, bod 2) — predmet nema ceho se chytit: adresa je
   * NAPSANA, ale nepotvrzena bodem z naseptavace (ani klikem do mapy) a
   * zaroven chybi parcela s katastralnim uzemim. Prazdny formular se takto
   * nehodnoti (tam je na rade hlaska „zadejte adresu nebo parcelu").
   */
  const subjectUnconfirmed =
    !hasPlace &&
    state.address.query.trim() !== '' &&
    (state.parcelNumber.trim() === '' || state.cadastralArea.trim() === '');

  /*
   * Krok GRAF5 — podklad panelu „Co víme z úředních zdrojů" a krokovniku.
   * „Ověřeno" = udaj prisel z UREDNIHO ZDROJE: adresa potvrzena bodem
   * (naseptavac RUIAN nebo klik do mapy), katastralni uzemi doplnene z te
   * adresy nebo z potvrzene parcely, parcela potvrzena z mapy.
   */
  const parcelFromRegistry = resolvedParcel !== null;
  const sourceRows = [
    { label: t('webAdmin.wizard.sourcesAddress', cs), value: state.address.query, verified: hasPlace },
    {
      label: WIZARD_TEXTS.cadastralAreaLabel,
      value: state.cadastralArea,
      verified: state.cadastralArea.trim() !== '' && (cadastralFromAddress || parcelFromRegistry),
    },
    {
      label: WIZARD_TEXTS.parcelNumberLabel,
      value: state.parcelNumber,
      verified: state.parcelNumber.trim() !== '' && parcelFromRegistry,
    },
  ];

  const subjectReady = hasPlace || (state.parcelNumber.trim() !== '' && state.cadastralArea.trim() !== '');
  const clientReady =
    state.client.type === 'COMPANY'
      ? state.client.companyName.trim() !== ''
      : state.client.personName.trim() !== '';
  const priceReady = state.priceExpectation.trim() !== '' || state.notes.trim() !== '';
  const stepState = (done: boolean, previousDone: boolean): V4Step['state'] =>
    done ? 'done' : previousDone ? 'now' : 'wait';
  const wizardSteps: readonly V4Step[] = [
    { id: 'property', label: t('webAdmin.wizard.stepProperty', cs), state: stepState(subjectReady, true), badge: '1' },
    { id: 'client', label: t('webAdmin.wizard.stepClient', cs), state: stepState(clientReady, subjectReady), badge: '2' },
    { id: 'price', label: t('webAdmin.wizard.stepPrice', cs), state: stepState(priceReady, clientReady), badge: '3' },
    {
      id: 'done',
      label: t('webAdmin.wizard.stepDone', cs),
      /* „Hotovo" je az ZALOZENA zakazka — do te doby ceka. */
      state: create.kind === 'submitting' ? 'now' : 'wait',
      badge: '4',
    },
  ];

  return (
    <form
      data-testid="zakazka-novy-page"
      onSubmit={(event) => {
        // Krok UXP4c (P3, nalez „Enter v poli neodešle formulář"): obalka je
        // skutecny `<form>`, takze Enter v textovem poli ho VYDA — bez
        // `<form>` to zadny browser nedela sam.
        event.preventDefault();
        handleCreate();
      }}
    >
      <header className="mb-4">
        <h1 className="rh-overview__title">{t('webAdmin.wizard.createCase', cs)}</h1>
        <p className="text-sm m-0 mt-1" style={{ color: 'var(--rh-text-muted)' }}>
          {t('webAdmin.wizard.lede', cs)}
        </p>
      </header>

      {/*
        Krok GRAF5 — KROKOVNIK PREDLOHY `maklerov-grafika-formular`.
        Jsou to SKUTECNE kroky dnesniho formulare (nemovitost → klient → cena
        → zalozeno), ne nove obrazovky: formular je od kroku UXZ1 jedna
        stranka (ctyrkrokovy pruvodce zanikl) a krokovnik ji jen MAPUJE, aby
        makler videl, co uz vyplnil. Krok se oznaci za hotovy, teprve kdyz je
        v nem udaj — nikoli tim, ze se kolem nej prorolovalo.
      */}
      <div className="mb-4">
        <V4Steps steps={wizardSteps} testId="wizard-steps" />
      </div>

      <FormLayout
        width="limited"
        testId="wizard-form"
        className="rh-wiz-form"
        aside={<OfficialSourcesPanel rows={sourceRows} />}
        main={
          <>
            <section className="rh-card">
              <FieldGrid>
                <div>
                  <p className="rh-case-card__metric-label" style={{ marginBottom: 6 }}>{WIZARD_TEXTS.intentLabel}</p>
                  <Segmented
                    label={WIZARD_TEXTS.intentLabel}
                    value={state.intent}
                    options={INTENT_OPTIONS}
                    testIdPrefix="wizard-intent"
                    onChange={(intent) => patch({ intent })}
                  />
                </div>
                <div>
                  <p className="rh-case-card__metric-label" style={{ marginBottom: 6 }}>{WIZARD_TEXTS.typeLabel}</p>
                  <Segmented
                    label={WIZARD_TEXTS.typeLabel}
                    value={state.kind}
                    options={TYPE_OPTIONS.map((opt) => ({ value: opt.kind, label: opt.label }))}
                    testIdPrefix="wizard-type"
                    onChange={(kind) => patch({ kind })}
                  />
                </div>
              </FieldGrid>
            </section>

            {/* Krok PRU1: cil „Ukaž mi" kroku „Založte zakázku z adresy". */}
            <section className="rh-card" data-guide="case">
              <h2 style={{ margin: '0 0 8px', fontSize: 'var(--rh-size-section)' }}>{WIZARD_TEXTS.addressLabel}</h2>
              <AddressAutocomplete value={state.address} onChange={handleAddressChange} />

              <FieldGrid className="mt-3">
                <label className="text-sm flex flex-col gap-1">
                  {WIZARD_TEXTS.parcelNumberLabel}
                  <input type="text" className="rh-input" value={state.parcelNumber} onChange={(e) => { setCadastralFromAddress(false); forgetResolvedParcel(); patch({ parcelNumber: e.target.value }); }} data-testid="wizard-parcel-number" />
                </label>
                <label className="text-sm flex flex-col gap-1">
                  {WIZARD_TEXTS.cadastralAreaLabel}
                  <input type="text" className="rh-input" value={state.cadastralArea} onChange={(e) => { setCadastralFromAddress(false); forgetResolvedParcel(); patch({ cadastralArea: e.target.value }); }} data-testid="wizard-cadastral-area" />
                </label>
                {state.kind === 'APARTMENT' ? (
                  <label className="text-sm flex flex-col gap-1">
                    {WIZARD_TEXTS.unitNumberLabel}
                    <input type="text" className="rh-input" value={state.unitNumber} onChange={(e) => patch({ unitNumber: e.target.value })} data-testid="wizard-unit-number" />
                  </label>
                ) : null}
                {/* Krok UXP4a bod 4 (nalez P2-17): byt se popisuje DISPOZICI,
                    u pozemku a domu by to bylo pole bez obsahu. */}
                {state.kind === 'APARTMENT' ? (
                  <label className="text-sm flex flex-col gap-1">
                    {WIZARD_TEXTS.layoutLabel}
                    <input type="text" className="rh-input" value={state.layout} placeholder={WIZARD_TEXTS.layoutPlaceholder} onChange={(e) => patch({ layout: e.target.value })} data-testid="wizard-layout" />
                  </label>
                ) : null}
              </FieldGrid>

              {cadastralFromAddress ? (
                <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-cadastral-from-address">
                  {WIZARD_TEXTS.cadastralFromAddress}
                </p>
              ) : null}

              {/*
                Krok PRF1 (vada V1, bod 2) — NEPOTVRZENA ADRESA SE NEDOMYSLI.
                Pruchod 13. 10.: „Úmyslovice" sedi na vic adresnich mist v
                RUZNYCH katastralnich uzemich; dokud si makler nevybere z
                naseptavace (adresni bod RUIAN → parcela a k. u. se doplni)
                nebo nenapise parcelu s uzemim, predmet zakazky nikdo
                neidentifikuje. Tady o tom stoji veta U POLE — ne ticho a ne
                prvni kandidat.
              */}
              {subjectUnconfirmed ? (
                <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-danger)' }} data-testid="wizard-address-unconfirmed">
                  {t('webAdmin.wizard.addressUnconfirmed', cs)}
                </p>
              ) : null}

              {/* Mapa az po vyberu mista (nalez GPT 13: prazdna mapa na uvod nic nerika). */}
              {hasPlace ? (
                <div className="mt-3" data-testid="wizard-map">
                  <MapPreview lat={state.address.lat} lon={state.address.lon} onPick={handleMapPick} />
                  <p className="text-sm m-0 mt-1" style={{ color: 'var(--rh-text-muted)' }}>{WIZARD_TEXTS.addressSourceCaption}</p>
                </div>
              ) : null}
              <ParcelPickStatus pick={parcelPick} onConfirm={handleConfirmParcel} />

              {/*
                Krok PRF3 bod 2 — POZEMKY DOMU. Dum se zaklada na stavebni
                parcele; zahrada a dvur jsou SAMOSTATNE parcely a bez nich
                report mluvi o vymere zastavene plochy, jako by to byla
                vymera nemovitosti. Nabidka je ze sousedu v RUIAN, vybira
                makler — vlastnictvi z otevrenych dat neplyne.
              */}
              {houseLandVisible ? (
                <div className="mt-3" data-testid="wizard-house-land">
                  <p className="rh-case-card__metric-label" style={{ marginBottom: 2 }}>
                    {t('webAdmin.wizard.houseLandTitle', cs)}
                  </p>
                  {houseLand.kind === 'loading' ? (
                    <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-house-land-loading">
                      {t('webAdmin.wizard.houseLandLoading', cs)}
                    </p>
                  ) : null}
                  {houseLand.kind === 'unavailable' ? (
                    <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-house-land-unavailable">
                      {t('webAdmin.wizard.houseLandUnavailable', cs)}
                    </p>
                  ) : null}
                  {houseLand.kind === 'ready' && houseLand.items.length === 0 ? (
                    <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-house-land-none">
                      {t('webAdmin.wizard.houseLandNone', cs)}
                    </p>
                  ) : null}
                  {houseLand.kind === 'ready' && houseLand.items.length > 0 ? (
                    <>
                      <p className="text-sm m-0 mb-2" style={{ color: 'var(--rh-text-muted)' }}>
                        {t('webAdmin.wizard.houseLandLede', cs)}
                      </p>
                      {houseLand.items.map((item) => (
                        <label key={item.ruianId} className="text-sm flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={houseLandPicked.includes(item.ruianId)}
                            onChange={() => toggleHouseLand(item.ruianId)}
                            data-testid={`wizard-house-land-${item.ruianId}`}
                          />
                          {houseLandOptionText(item)}
                        </label>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}

              {/* Souradnice, odkaz z mapy, „moje poloha" a inzerat — sbalene. */}
              <details className="mt-3" data-testid="wizard-more-options">
                <summary className="text-sm" style={{ cursor: 'pointer' }}>{t('webAdmin.wizard.moreOptions', cs)}</summary>
                <div className="mt-3" data-testid="wizard-location-input">
                  <label className="text-sm flex flex-col gap-1">
                    {t('webAdmin.wizard.location.coordinateLabel', cs)}
                    <span className="flex gap-2">
                      <input
                        type="text"
                        className="rh-input"
                        style={{ flex: 1 }}
                        value={coordinateText}
                        placeholder={t('webAdmin.wizard.location.coordinatePlaceholder', cs)}
                        onChange={(e) => setCoordinateText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyCoordinateText();
                          }
                        }}
                        data-testid="wizard-coordinate-input"
                      />
                      <button type="button" className="rh-btn" onClick={applyCoordinateText} data-testid="wizard-coordinate-apply">
                        {t('webAdmin.wizard.location.apply', cs)}
                      </button>
                      <button
                        type="button"
                        className="rh-btn"
                        onClick={handleLocate}
                        disabled={locateState === 'locating'}
                        data-testid="wizard-locate"
                      >
                        {locateState === 'locating'
                          ? t('webAdmin.wizard.location.locating', cs)
                          : t('webAdmin.wizard.location.locate', cs)}
                      </button>
                    </span>
                    <span className="text-sm" style={{ color: 'var(--rh-text-muted)' }}>
                      {t('webAdmin.wizard.location.coordinateHint', cs)}
                    </span>
                  </label>
                  {locationNoticeText(locationNotice) === null ? null : (
                    <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-danger)' }} role="status" data-testid="wizard-location-notice">
                      {locationNoticeText(locationNotice)}
                    </p>
                  )}
                  {locateState !== null && locateState !== 'locating' ? (
                    <p className="text-sm m-0 mt-2" style={{ color: 'var(--rh-danger)' }} role="status" data-testid="wizard-locate-failed">
                      {locateState === 'unsupported'
                        ? t('webAdmin.wizard.location.locateUnsupported', cs)
                        : t('webAdmin.wizard.location.locateFailed', cs)}
                    </p>
                  ) : null}
                  {located === null ? null : (
                    <p
                      className="text-sm m-0 mt-2"
                      style={{ color: isAccuracyWeak(located.accuracy) ? 'var(--rh-danger)' : 'var(--rh-text-muted)' }}
                      data-testid="wizard-locate-accuracy"
                    >
                      {isAccuracyWeak(located.accuracy)
                        ? t('webAdmin.wizard.location.accuracyCoarse', { ...cs, values: { metres: formatAccuracy(located.accuracy) } })
                        : t('webAdmin.wizard.location.accuracy', { ...cs, values: { metres: formatAccuracy(located.accuracy) } })}
                    </p>
                  )}
                </div>

                <FieldGrid className="mt-3">
                  <label className="text-sm flex flex-col gap-1">
                    {WIZARD_TEXTS.landRegistryLabel}
                    <input type="text" className="rh-input" value={state.landRegistryNumber} onChange={(e) => patch({ landRegistryNumber: e.target.value })} data-testid="wizard-land-registry" />
                  </label>
                  <label className="text-sm flex flex-col gap-1">
                    {WIZARD_TEXTS.listingUrlLabel}
                    <span className="flex gap-2">
                      <input
                        type="url"
                        className="rh-input"
                        style={{ flex: 1 }}
                        placeholder={WIZARD_TEXTS.listingUrlPlaceholder}
                        value={state.listingUrl}
                        onChange={(e) => { patch({ listingUrl: e.target.value }); setListing({ kind: 'idle' }); }}
                        data-testid="wizard-listing-url"
                      />
                      <button type="button" className="rh-btn" onClick={() => void handleListingLoad()} data-testid="wizard-listing-load">
                        {WIZARD_TEXTS.listingLoadButton}
                      </button>
                    </span>
                  </label>
                </FieldGrid>
                <ListingStatus state={listing} />
              </details>
            </section>

            <section className="rh-card">
              <h2 style={{ margin: '0 0 8px', fontSize: 'var(--rh-size-section)' }}>{WIZARD_TEXTS.clientLabel}</h2>
              <ClientSection
                client={state.client}
                onChange={(client) => {
                  setPhoneError(undefined);
                  patch({ client });
                }}
                phoneError={phoneError}
              />
              <p className="text-sm m-0 mt-3" data-testid="wizard-broker">
                <span style={{ color: 'var(--rh-text-muted)' }}>{WIZARD_TEXTS.brokerLabel}: </span>
                {brokerName ?? WIZARD_TEXTS.brokerUnassigned}
              </p>
            </section>

            <section className="rh-card">
              <FieldGrid>
                <div className="text-sm flex flex-col gap-1">
                  <label className="flex flex-col gap-1">
                    {WIZARD_TEXTS.priceExpectationLabel}
                    <input type="text" className="rh-input" value={state.priceExpectation} onChange={(e) => patch({ priceExpectation: e.target.value })} data-testid="wizard-price-expectation" />
                  </label>
                  {/* Krok PROP3 bod 3: cislo z prvniho hovoru je PREDSTAVA MAJITELE.
                      Nabidkovou cenou inzeratu a verejne stranky se stane jen tady. */}
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={state.priceIsAsking}
                      onChange={(e) => patch({ priceIsAsking: e.target.checked })}
                      data-testid="wizard-price-is-asking"
                    />
                    {WIZARD_TEXTS.priceIsAskingLabel}
                  </label>
                  <span style={{ color: 'var(--rh-text-muted)' }}>{WIZARD_TEXTS.priceIsAskingHint}</span>
                </div>
                <label className="text-sm flex flex-col gap-1">
                  {WIZARD_TEXTS.notesLabel}
                  <textarea className="rh-textarea" rows={3} placeholder={WIZARD_TEXTS.notesPlaceholder} value={state.notes} onChange={(e) => patch({ notes: e.target.value })} data-testid="wizard-notes" />
                </label>
              </FieldGrid>
            </section>
          </>
        }
        actions={
          <>
            {create.kind === 'error' ? (
              <p className="rh-a2-error m-0" role="alert" data-testid="wizard-create-error">{create.message}</p>
            ) : create.kind === 'parcels_already_used' ? (
              <p className="rh-a2-error m-0" role="alert" data-testid="wizard-parcels-already-used">
                {t('webAdmin.wizard.alreadyUsedProperty', cs)}
                {create.propertyIds.map((propertyId) => (
                  <React.Fragment key={propertyId}>
                    {' '}
                    <Link
                      to="/nemovitosti/$id"
                      params={{ id: propertyId }}
                      data-testid={`wizard-parcels-already-used-open-${propertyId}`}
                    >
                      {t('webAdmin.wizard.alreadyUsedOpen', cs)}
                    </Link>
                  </React.Fragment>
                ))}
              </p>
            ) : create.kind === 'same_property_ask' ? (
              /*
               * Krok P8F1 bod 1b: makler vidi, CO nasla kancelar — adresu,
               * jednotku a dispozici karty — a rozhodne. „Ne" zaklada novou
               * kartu, „Ano" pouzije tu nalezenou. Tise to rozhodnout nesmime
               * (vada P8-N1: byt 1175/3 dostal cenu bytu 1175/12).
               */
              <div role="alert" data-testid="wizard-same-property-ask" className="flex flex-col gap-2">
                <p className="rh-a2-error m-0">{t('webAdmin.wizard.samePropertyAsk', cs)}</p>
                <p className="text-sm m-0" data-testid="wizard-same-property-candidate">
                  {[
                    create.candidate.address,
                    create.candidate.units.length > 0
                      ? `${WIZARD_TEXTS.unitNumberLabel}: ${create.candidate.units.join(', ')}`
                      : t('webAdmin.wizard.samePropertyNoUnit', cs),
                    create.candidate.disposition,
                  ]
                    .filter((part): part is string => part !== null && part !== '')
                    .join(' · ')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => { handleCreate({ propertyId: create.candidate.propertyId, same: true }); }}
                    data-testid="wizard-same-property-yes"
                  >
                    {t('webAdmin.wizard.samePropertyYes', cs)}
                  </button>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => { handleCreate({ propertyId: create.candidate.propertyId, same: false }); }}
                    data-testid="wizard-same-property-no"
                  >
                    {t('webAdmin.wizard.samePropertyNo', cs)}
                  </button>
                </div>
              </div>
            ) : create.kind === 'existing_client_ask' ? (
              /*
               * Krok P8F6 bod 2: e-mail uz v kancelari nekdo ma. Vybira
               * MAKLER — tiche slouceni ani tichy duplikat nejsou volby
               * (shoda oponentury 27. 10.).
               */
              <div role="alert" data-testid="wizard-existing-client-ask" className="flex flex-col gap-2">
                <p className="rh-a2-error m-0">
                  {t('webAdmin.wizard.existingClientAsk', {
                    ...cs,
                    values: { name: create.party.displayName },
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => {
                      clientChoice.current = { partyId: create.party.partyId };
                      setCreate({ kind: 'submitting' });
                      submitCase();
                    }}
                    data-testid="wizard-existing-client-use"
                  >
                    {t('webAdmin.wizard.existingClientUse', cs)}
                  </button>
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => {
                      clientChoice.current = { partyId: null };
                      setCreate({ kind: 'submitting' });
                      submitCase();
                    }}
                    data-testid="wizard-existing-client-new"
                  >
                    {t('webAdmin.wizard.existingClientNew', cs)}
                  </button>
                </div>
              </div>
            ) : create.kind === 'facts_failed' ? (
              /*
               * Krok P8F6 bod 1: zakazka JE zalozena, ale udaj na karte
               * nemovitosti chybi. Hlaska rika KTERY udaj (katalogovy popisek
               * `property.fact.*`, jeden zdroj s naborem a detailem
               * nemovitosti) a nabizi opakovani pod TYMZ klicem odeslani.
               */
              <div role="alert" data-testid="wizard-facts-failed" className="flex flex-col gap-2">
                <p className="rh-a2-error m-0">
                  {t('webAdmin.wizard.factsFailed', {
                    ...cs,
                    values: { fields: create.factKeys.map((key) => fieldLabel(key)).join(', ') },
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rh-btn"
                    onClick={() => {
                      setCreate({ kind: 'submitting' });
                      submitCase();
                    }}
                    data-testid="wizard-facts-retry"
                  >
                    {t('webAdmin.wizard.factsRetry', cs)}
                  </button>
                  <Link
                    to="/zakazky/$id"
                    params={{ id: create.caseId }}
                    className="rh-btn"
                    data-testid="wizard-facts-open-case"
                  >
                    {t('webAdmin.wizard.factsOpenCase', cs)}
                  </Link>
                </div>
              </div>
            ) : (
              <p className="text-sm m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="wizard-create-hint">
                {t('webAdmin.wizard.createCaseHint', cs)}
              </p>
            )}
            <button
              type="submit"
              className="rh-btn rh-btn--primary"
              disabled={create.kind === 'submitting'}
              data-testid="wizard-create-case"
            >
              {create.kind === 'submitting' ? t('webAdmin.wizard.creating', cs) : t('webAdmin.wizard.createCase', cs)}
            </button>
          </>
        }
      />
    </form>
  );
}
